

import { useState, useCallback, useEffect, SyntheticEvent, useRef } from "react";

// Serializers
import BroadcastPostSerializer from "@/issara-sdk/types/BroadcastPostSerializer_apps_PRX";
import DivisionSerializer from "@/issara-sdk/types/DivisionSerializer_core";

// Utils
import { getPostById } from "./MeBroadcastUtils";
import { formatDateToYYYYMMDD } from "react-lib/utils/dateUtils";

// Types
import { 
  RunSequence as MeFormRunSequence,
  COLLECTION,
  BroadcastDataFirebase
} from "./MeFormType";
import {
  RunSequence as MeCastRunSequence,
  ActionPostBroadcast,
  BroadcastPostParams,
  INIT_BROADCAST_APPROVAL_FILTER,
  INIT_BROADCAST_LIST_FILTER,
  MemberGroupListParams,
  BroadcastMode,
  MemberGroupNodeParams,
  MemberGroupListNodesItem,
  SelectedMemberNode
} from "./MeCastType";
import moment from "moment";
import { BROADCAST_HEADER } from "./sequence/MeCast";
import PublishedDiagRuleWithNodeSerializer from "@/issara-sdk/types/PublishedDiagRuleWithNodeSerializer_apps_PRX";

type PreviewData = {
  text: string;
  media: string | null;
};

type UseBroadcastPreviewsProps = {
  items: BroadcastPostSerializer[];
};

type UseBroadcastApprovalFilterProps = {
  divisionList: DivisionSerializer[];
};

type UseBroadcastApprovalActionProps = {
  runSequence: MeCastRunSequence;
  onActionComplete?: () => void;
};

type UseBroadcastApprovalDetailProps = {
  runSequence: MeCastRunSequence;
};

type UseDivisionMapProps = {
  data: BroadcastPostSerializer[];
  divisionList: DivisionSerializer[];
  runSequence: MeCastRunSequence;
};

type UseBroadcastContentProps = Partial<{
  firebaseId: string;
  collection: string;
}>;

type UseBroadcastListFilterProps = {
  selectedDivisionId: number;
};

type UseBroadcastListEditorProps = {
  onFetchList?: () => void;
};

type MemberNodeSaveItem = {
  id: number;
  active: boolean;
};

type UseMemberNodeEditorProps = {
  runSequence: MeCastRunSequence;
  onSaveComplete?: () => void;
};

const formatPreviewContent = (content: BroadcastDataFirebase): PreviewData => {
  const text = content?.previews_text
    ? content.previews_text.slice(0, 30) + "..."
    : content?.content
    ? content.content.replace(/<[^>]+>/g, "").slice(0, 175) + "..."
    : "";
  
  const media = content?.previews_video?.[0] || content?.previews_picture?.[0] || null;

  return { text, media };
};

/**========================================================================
 * *                                INFO
 *   
 *   On Use at : CardMeCastList, CardMeCastBroadcastApproval
 *
 *========================================================================**/

export const useBroadcastPreviews = ({ items }: UseBroadcastPreviewsProps) => {
  const [previews, setPreviews] = useState<Record<string, PreviewData>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // ----- Fetch Singer Preview ----- //
  const fetchPreview = useCallback(async (firebaseId: string) => {
    try {
      const content = await getPostById(COLLECTION, firebaseId);

      if (!content) return null;

      return formatPreviewContent(content);
    } catch (err) {
      console.error(`Failed to fetch preview for ${firebaseId}: `, err);
      
      return null;
    };
  }, []);

  // ----- Fetch All Previews For Items ----- //
  useEffect(() => {
    const fetchAllPreviews = async () => {
      const itemsToFetch = items.filter(
        (row) => row.firebase_id && !previews[row.firebase_id]
      );

      if (itemsToFetch.length === 0) return;

      setLoading(true);
      setError(null);

      try {
        const results = await Promise.allSettled(
          itemsToFetch.map(async (row) => {
            const preview = await fetchPreview(row.firebase_id);

            return {
              firebaseId: row.firebase_id,
              preview,
            };
          })
        );

        setPreviews((prev) => {
          const newPreviews = { ...prev };

          results.forEach((result) => {
            if (result.status === "fulfilled" && result.value.preview) {
              newPreviews[result.value.firebaseId] = result.value.preview;
            }
          });

          return newPreviews;
        });
      } catch (err) {
        setError("Failed to fetch some previews");
        console.error("Error fetching previews: ", err);
      } finally {
        setLoading(false);
      };
    };

    fetchAllPreviews();
  }, [items, fetchPreview]);

  // ----- Get Preview by firebase_id ----- //
  const getPreview = useCallback(
    (firebaseId: string): PreviewData | undefined => {
      return previews[firebaseId];
    },
    [previews]
  );

  // ----- Clear All Previews ----- //
  const clearPreviews = useCallback(() => {
    setPreviews({});
  }, []);
  
  const refreshPreview = useCallback(
    async (firebaseId: string) => {
      const preview = await fetchPreview(firebaseId);

      if (preview) {
        setPreviews((prev) => ({
          ...prev,
          [firebaseId]: preview,
        }));
      }
    },
    [fetchPreview]
  );

  return {
    // state
    previews,
    loading,
    error,

    // actions
    getPreview,
    clearPreviews,
    refreshPreview,
  };
};

/**========================================================================
 * *                                INFO
 *   
 *   On Use at : CardMeCastBroadcastApproval
 *
 *========================================================================**/

export const useBroadcastApprovalFilter = ({ divisionList }: UseBroadcastApprovalFilterProps) => {
  const [filter, setFilter] = useState<BroadcastPostParams>(INIT_BROADCAST_APPROVAL_FILTER);
  const [activePage, setActivePage] = useState<number>(1);

  // ----- Handlers ----- //
  const handleChangeData = useCallback((e: SyntheticEvent | null, data: any) => {
    const { name, value } = data;
    setFilter(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleChangeDate = useCallback((name: string, date: string) => {
    setFilter(prev => ({ ...prev, [name]: date }));
  }, []);

  const handleToggleStatus = useCallback((value: BroadcastPostSerializer["status"]) => {
    setFilter(prev => {
      const statuses = prev.statuses ?? [];
      
      return statuses.includes(value)
        ? { ...prev, statuses: statuses.filter(s => s !== value) }
        : { ...prev, statuses: [...statuses, value] }
    });
  }, []);

  const handleChangePage = useCallback((page: number) => {
    setActivePage(page);
  }, []);

  const resetFilter = useCallback(() => {
    setFilter(INIT_BROADCAST_APPROVAL_FILTER);
    setActivePage(1);
  }, []);

  // ----- Utils ----- //
  const getDivisionIdByName = useCallback((oaName: any): number | undefined => {
    if (!oaName) return undefined;

    const name = typeof oaName === "string"
      ? oaName
      : oaName.value || oaName.text;
    
    const division = divisionList.find(
      (d: any) => d?.name?.toLowerCase() === name?.toLowerCase()
    );

    return division?.id ?? undefined;
  }, [divisionList]);

  const getResolvedParams = useCallback((overrides?: Partial<BroadcastPostParams>): BroadcastPostParams => {
      const merged = { ...filter, ...overrides };
    return {
      ...merged,
      division: getDivisionIdByName(filter.division),
      ...(((merged.statuses ?? []).includes("REVIEWED") && !(merged.statuses ?? []).includes("BROADCASTED"))
        ? { statuses: [...(merged.statuses ?? []), "BROADCASTED"] }
        : {}),
      schedule_broadcasted_start_date: merged.schedule_broadcasted_start_date ? formatDateToYYYYMMDD(merged.schedule_broadcasted_start_date) : "",
      schedule_broadcasted_end_date: merged.schedule_broadcasted_end_date ? formatDateToYYYYMMDD(merged.schedule_broadcasted_end_date) : "",
      
    };
  }, [filter, getDivisionIdByName]);

  return {
    // state
    filter,
    activePage,

    // setter
    setFilter,
    setActivePage,

    // handlers
    handleChangeData,
    handleChangeDate,
    handleToggleStatus,
    handleChangePage,
    resetFilter,

    // utils
    getDivisionIdByName,
    getResolvedParams
  };
};

/**========================================================================
 * *                                INFO
 *   
 *   On Use at : CardMeCastBroadcastApproval
 *
 *========================================================================**/

export const useBroadcastApprovalActions = ({
  runSequence,
  onActionComplete
}: UseBroadcastApprovalActionProps) => {
  const [selectedBroadcast, setSelectedBroadcast] = useState<BroadcastPostSerializer | null>(null);
  const [actionPost, setActionPost] = useState<ActionPostBroadcast | null>(null);
  const [isActionModOpen, setIsActionModOpen] = useState<boolean>(false);
  const [rejectRemark, setRejectRemark] = useState<string>("");

  // ----- Modal Handlers ----- //
  const openActionModal = useCallback((action: ActionPostBroadcast) => {
    setActionPost(action);
    setIsActionModOpen(true);
  }, []);

  const closeActionModal = useCallback(() => {
    setActionPost(null);
    setIsActionModOpen(false);
    setRejectRemark("");
  }, []);

  // ----- Actions Handlers ----- //
  const handleApprove = useCallback((broadcastPostId: number) => {
    runSequence({
      sequence: "MeCast",
      action: "ACTION_BROADCAST",
      broadcastPostId,
      actionParams: { action: "APPROVE" },
    });
    onActionComplete?.();
  }, [runSequence, onActionComplete]);

  const handleReject = useCallback((broadcastPostId: number, remark: string) => {
    runSequence({
      sequence: "MeCast",
      action: "ACTION_BROADCAST",
      broadcastPostId,
      actionParams: { action: "REJECT", remark },
    });
    onActionComplete?.();
  }, [runSequence, onActionComplete]);

  const handleUnapprove = useCallback((broadcastPostId: number) => {
    runSequence({
      sequence: "MeCast",
      action: "ACTION_BROADCAST",
      broadcastPostId,
      actionParams: { action: "UNAPPROVE" }
    });
    onActionComplete?.();
  }, [runSequence, onActionComplete]);

  const confirmAction = useCallback(() => {
    if (!actionPost || !selectedBroadcast?.id) return;

    switch (actionPost) {
      case "APPROVE":
        handleApprove(selectedBroadcast.id);
        break;
      case "REJECT":
        handleReject(selectedBroadcast.id, rejectRemark);
        break;
      case "UNAPPROVE":
        handleUnapprove(selectedBroadcast.id);
        break;
    }

    closeActionModal();
  }, [actionPost, selectedBroadcast, rejectRemark, handleApprove, handleReject, handleUnapprove, closeActionModal]);

  const handleChangeRemark = useCallback((value: string) => {
    setRejectRemark(value);
  }, []);

  return {
    // state
    selectedBroadcast,
    actionPost,
    isActionModOpen,
    rejectRemark,

    // setters
    setSelectedBroadcast,

    // handlers
    openActionModal,
    closeActionModal,
    confirmAction,
    handleChangeRemark,
    handleApprove,
    handleReject,
    handleUnapprove
  };
};

/**========================================================================
 * *                                INFO
 *   
 *   On Use at : CardMeCastBroadcastApproval
 *
 *========================================================================**/

export const useBroadcastApprovalDetail = ({
  runSequence
}: UseBroadcastApprovalDetailProps) => {
  const [isDetailOpen, setIsDetailOpen] = useState<boolean>(false);
  const [isLogOpen, setIsLogOpen] = useState<boolean>(false);
  const [selectedBroadcast, setSelectedBroadcast] = useState<BroadcastPostSerializer | null>(null);

  // ----- Detail Handlers ----- //
  const openDetail = useCallback(async (broadcastPostId?: number) => {
    if (!broadcastPostId) return;

    const broadcastDetail = await runSequence({
      sequence: "MeCast",
      action: "GET_BROADCAST_POST_DETAIL",
      broadcastPostId,
    });

    if (!broadcastDetail) return;

    setSelectedBroadcast(broadcastDetail);
    setIsDetailOpen(true);
  }, [runSequence]);

  const closeDetail = useCallback(() => {
    setSelectedBroadcast(null);
    setIsDetailOpen(false);
  }, []);

  // ----- Log Handlers ----- //
  const openLog = useCallback((broadcastPostId?: number) => {
    if (!broadcastPostId) return;

    runSequence({
      sequence: "MeCast",
      action: "GET_BROADCAST_LOG",
      broadcastPostId,
    });

    setIsLogOpen(true);
  }, [runSequence]);

  const closeLog = useCallback(() => {
    setIsLogOpen(false);
  }, []);

  return {
    // state
    isDetailOpen,
    isLogOpen,
    selectedBroadcast,

    // setters
    setSelectedBroadcast,

    // handlers
    openDetail,
    closeDetail,
    openLog,
    closeLog
  };
};

/**========================================================================
 * *                                INFO
 *   
 *   On Use at : CardMeCastBroadcastApproval
 *
 *========================================================================**/

export const useDivisionMap = ({ data, divisionList, runSequence }: UseDivisionMapProps) => {
  const [divisionMap, setDivisionMap] = useState<Record<number, any>>({});
  
  const requestedDivisions = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!data?.length) return;

    data.forEach((row: any) => {
      if (!row.division) return;

      const existing = divisionList.find((d: any) => d.id === row.division);

      if (existing && !divisionMap[row.division]) {
        setDivisionMap(prev => ({ ...prev, [row.division]: existing }));
        return;
      }

      if (!existing && !requestedDivisions.current.has(row.division)) {
        requestedDivisions.current.add(row.division);
        runSequence({
          sequence: "MeCast",
          action: "GET_DIVISION_DETAIL",
          id: row.division,
        });
      }
    });
  }, [data, divisionList, runSequence]);

  const updateDivisionMap = useCallback((detail: any) => {
    if (detail) {
      setDivisionMap(prev => ({ ...prev, [detail.id]: detail }));
    }
  }, []);

  const resetDivisionMap = useCallback(() => {
    requestedDivisions.current = new Set();
    setDivisionMap({});
  }, []);

  return {
    divisionMap,
    updateDivisionMap,
    resetDivisionMap,
  };
};

/**========================================================================
 * *                                INFO
 *   
 *   On Use at : CardMeCastBroadcastApproval
 *
 *========================================================================**/

export const useBroadcastContent = ({
  firebaseId,
  collection = COLLECTION
}: UseBroadcastContentProps) => {
  const [content, setContent] = useState<{ text: string; media: string[] }>({
    text: "",
    media: [],
  });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchContent = async () => {
      if (!firebaseId) {
        setContent({ text: "", media: [] });

        return;
      };

      setLoading(true);
      try {
        const data = await getPostById(collection, firebaseId);
        if (data) {
          setContent({
            text: data.content || "",
            media: data.previews_picture || data.previews_video || [],
          });
        }
      } catch (error) {
        console.error("Failed to fetch broadcast content:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [firebaseId, collection]);

  return { content, loading };
};

/**========================================================================
 * *                                INFO
 *   
 *   On Use at : CardMeCastBroadcastApproval
 *
 *========================================================================**/

export const useBroadcastListFilter = ({ selectedDivisionId }: UseBroadcastListFilterProps) => {
  const [filter, setFilter] = useState<BroadcastPostParams>({
    ...INIT_BROADCAST_LIST_FILTER,
    division: selectedDivisionId,
  });
  const [activePage, setActivePage] = useState<number>(1);
  const [latestRefresh, setLatestRefresh] = useState<string>(moment().format("DD/MM/YYYY \\[HH:mm\\]"));

  // ----- Handlers ----- //
  const handleChangeData = useCallback((e: SyntheticEvent | null, data: any) => {
    const { name, value } = data;
    setFilter(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleChangeDate = useCallback((name: string, date: string) => {
    setFilter(prev => ({ ...prev, [name]: date }));
  }, []);

  const handleToggleStatus = useCallback((values: BroadcastPostSerializer["status"][]) => {
    setFilter(prev => {
      const current = prev.statuses ?? [];
      const isActive = values.every(v => current.includes(v));

      return {
        ...prev,
        statuses: isActive
          ? current.filter(s => !values.includes(s))
          : Array.from(new Set([...current, ...values]))
      };
    });
  }, []);

  const handleChangePage = useCallback((page: number) => {
    setActivePage(page);
  }, []);

  const handleRefresh = useCallback(() => {
    const currentDateTime = moment().format("DD/MM/YYYY \\[HH:mm\\]");
    setLatestRefresh(currentDateTime);
  }, []);

  const resetFilter = useCallback(() => {
    setFilter({
      ...INIT_BROADCAST_LIST_FILTER,
      division: selectedDivisionId,
    });
    setActivePage(1);
  }, [selectedDivisionId]);

  const getResolvedParams = useCallback((overrides?: Partial<BroadcastPostParams>): BroadcastPostParams => {
    const merged = { ...filter, ...overrides };
    return {
      ...merged,
      division: selectedDivisionId,
      schedule_broadcasted_start_date: merged.schedule_broadcasted_start_date ? formatDateToYYYYMMDD(merged.schedule_broadcasted_start_date) : "",
      schedule_broadcasted_end_date: merged.schedule_broadcasted_end_date ? formatDateToYYYYMMDD(merged.schedule_broadcasted_end_date) : "",
    };
  }, [filter, selectedDivisionId]);

  return {
    filter,
    activePage,
    latestRefresh,
    setFilter,
    setActivePage,
    handleChangeData,
    handleChangeDate,
    handleToggleStatus,
    handleChangePage,
    handleRefresh,
    resetFilter,
    getResolvedParams
  };
};

/**========================================================================
 * *                                INFO
 *   
 *   On Use at : CardMeCastBroadcastApproval
 *
 *========================================================================**/

export const useMemberGroupListFilter = () => {
  const [filter, setFilter] = useState<MemberGroupListParams>({});
  const [activePage, setActivePage] = useState<number>(1);
  const [latestRefresh, setLatestRefresh] = useState<string>(moment().format("DD/MM/YYYY \\[HH:mm\\]"));

  // ----- Handlers ----- //
  const handleChangeData = useCallback((e: SyntheticEvent | null, data: any) => {
    const { name, value } = data;
    setFilter(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleChangeDate = useCallback((name: string, date: string) => {
    setFilter(prev => ({ ...prev, [name]: date }));
  }, []);

  const handleChangePage = useCallback((page: number) => {
    setActivePage(page);
  }, []);

  const handleRefresh = useCallback(() => {
    const currentDateTime = moment().format("DD/MM/YYYY \\[HH:mm\\]");
    setLatestRefresh(currentDateTime);
  }, []);

  const resetFilter = useCallback(() => {
    setFilter({});
    setActivePage(1);
  }, []);

  const getResolvedParams = useCallback((overrides?: Partial<MemberGroupListParams>): MemberGroupListParams => {
    return {
      ...filter,
      ...overrides,
    };
  }, [filter]);

  return {
    filter,
    activePage,
    latestRefresh,
    setFilter,
    setActivePage,
    handleChangeData,
    handleChangeDate,
    handleChangePage,
    handleRefresh,
    resetFilter,
    getResolvedParams
  };
};

/**========================================================================
 * *                                INFO
 *   
 *   On Use at : CardMeCastBroadcastApproval
 *
 *========================================================================**/

export const useBroadcastListEditor = (props: UseBroadcastListEditorProps) => {
  // ----- Editor State ----- //
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [mode, setMode] = useState<BroadcastMode>("CREATE");
  const [header, setHeader] = useState<string>(BROADCAST_HEADER.CREATE);
  const [selectedBroadcast, setSelectedBroadcast] = useState<BroadcastPostSerializer | undefined>(undefined);
  
  // ----- In Review Popup State ----- //
  const [isInReviewOpen, setIsInReviewOpen] = useState<boolean>(false);
  const prevBroadcastStatusRef = useRef<BroadcastPostSerializer["status"] | undefined>(undefined);

  // ----- Handlers ----- //
  const openEditor = useCallback((editorMode: BroadcastMode, broadcast?: BroadcastPostSerializer) => {
    setMode(editorMode);
    setHeader(BROADCAST_HEADER[editorMode]);
    setSelectedBroadcast(broadcast);
    prevBroadcastStatusRef.current = broadcast?.status;
    setIsOpen(true);
  }, []);

  const closeEditor = useCallback(() => {
    setIsOpen(false);
    setSelectedBroadcast(undefined);
    setMode("CREATE");
    setHeader(BROADCAST_HEADER["CREATE"]);
    setIsInReviewOpen(false);

    // ? Refresh list after closing
    props.onFetchList?.();
  }, [props.onFetchList]);

  const selectBroadcast = useCallback((broadcast: BroadcastPostSerializer, editorMode: BroadcastMode = "VIEW") => {
    setSelectedBroadcast(broadcast);
    openEditor(editorMode, broadcast);
  }, [openEditor]);

  const updateSelectedBroadcast = useCallback((broadcast: BroadcastPostSerializer | undefined) => {
    setSelectedBroadcast(broadcast);

    if (broadcast) {
      setHeader(BROADCAST_HEADER.EDIT);
    }
  }, []);

  // ----- In Review Handlers ----- //
  const openInReviewPopup = useCallback(() => {
    setIsInReviewOpen(true);
  }, []);

  const closeInReviewPopup = useCallback(() => {
    setIsInReviewOpen(false);
  }, []);

  const checkAndShowInReview = useCallback((broadcast: BroadcastPostSerializer | undefined, currentMode: BroadcastMode) => {
    const isStatusChangedToReview = broadcast?.status === "PENDING_REVIEW" && prevBroadcastStatusRef.current !== "PENDING_REVIEW";
    
    // update the ref to prevent multiple triggers
    if (broadcast) {
      prevBroadcastStatusRef.current = broadcast.status;
    }

    if (isStatusChangedToReview) {
      setIsInReviewOpen(true);
      return true;
    }
    return false;
  }, []);

  return {
    // Editor State
    isOpen,
    mode,
    header,
    selectedBroadcast,

    // In Review State
    isInReviewOpen,

    // Editor Actions
    openEditor,
    closeEditor,
    selectBroadcast,
    updateSelectedBroadcast,
    setSelectedBroadcast,

    // In Review Actions
    openInReviewPopup,
    closeInReviewPopup,
    checkAndShowInReview,
  };
};

/**========================================================================
 * *                                INFO
 *   
 *   On Use at : CardMeCastBroadcastApproval
 *
 *========================================================================**/

export const useMemberNodeEditor = (props?: UseMemberNodeEditorProps) => {
  // ----- Modal State ----- //
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedNode, setSelectedNode] = useState<SelectedMemberNode | null>(null);

  // ----- Filter ----- //
  const [filter, setFilter] = useState<MemberGroupNodeParams>({});
  const [latestRefresh, setLatestRefresh] = useState<string>(moment().format("DD/MM/YYYY \\[HH:mm\\]"));

  // ----- Changed Members ----- //
  const [changedMembers, setChangedMembers] = useState<Map<number, boolean>>(new Map());

  const pdrNodeId = selectedNode?.node?.node;

  // ----- Modal Handlers ----- //
  const openModal = useCallback((
    group: PublishedDiagRuleWithNodeSerializer,
    node: MemberGroupListNodesItem
  ) => {
    setSelectedNode({ group, node });
    setFilter({});
    setChangedMembers(new Map());
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setSelectedNode(null);
    setFilter({});
    setChangedMembers(new Map());
  }, []);

  // ----- Filter Handlers ----- //
  const handleChangeData = useCallback((e: SyntheticEvent | null, data: any) => {
    const { name, value } = data;
    setFilter(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleToggleShowInactive = useCallback((checked: boolean) => {
    setFilter(prev => {
      if (checked) {
        return { ...prev, active: false };
      } else {
        const { active, ...rest } = prev;
        return rest;
      }
    });
  }, []);

  const handleRefresh = useCallback(() => {
    const currentDateTime = moment().format("DD/MM/YYYY \\[HH:mm\\]");
    setLatestRefresh(currentDateTime);
  }, []);

  const resetFilter = useCallback(() => {
    setFilter({});
  }, []);

  const getResolvedParams = useCallback(
    (overrides?: Partial<MemberGroupNodeParams>): MemberGroupNodeParams => {
      return {
        ...filter,
        ...overrides,
      };
    },
    [filter]
  );

  // ----- Member Change Handlers ----- //
  const handleMemberActiveChange = useCallback((memberId: number, active: boolean) => {
    setChangedMembers(prev => {
      const newMap = new Map(prev);
      newMap.set(memberId, active);
      return newMap;
    });
  }, []);

  const resetChangedMembers = useCallback(() => {
    setChangedMembers(new Map());
  }, []);

  // ----- Get Save Data ----- //
  const getSaveData = useCallback((): MemberNodeSaveItem[] => {
    return Array.from(changedMembers.entries()).map(([id, active]) => ({
      id,
      active,
    }));
  }, [changedMembers]);

  // ----- Check if has changes ----- //
  const hasChanges = changedMembers.size > 0;

  return {
    // modal state
    isOpen,
    selectedNode,
    pdrNodeId,

    // filter state
    filter,
    latestRefresh,

    // changed member state
    changedMembers,
    hasChanges,

    // modal actions
    openModal,
    closeModal,

    // filter actions
    handleChangeData,
    handleToggleShowInactive,
    handleRefresh,
    resetFilter,
    getResolvedParams,

    // member change actions
    handleMemberActiveChange,
    resetChangedMembers,
    getSaveData,
  };
};